import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useApi from "../../../utils/api";
import { getErrorMessage } from "../../../utils/helpers/errors";
import { useProfile } from "../context";

export const useWorkingShiftsApi = () => {
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const [totalItems, setTotalItems] = useState();
    const [loading, setLoading] = useState(false);

    const { authGet, authPost, authPut, authDelete } = useApi();
    const { user, isSelf } = useProfile();

    const fetch = useCallback(async (params, { controller }) => {
        const url = isSelf ? "/profile/working-shift" : `/users/${user?.id}/working-shift`;
        setLoading(true);
        try {
            if (user?.id) {
                const response = await authGet(url, {
                    params,
                    signal: controller.signal
                });
                if (response) {
                    setData(response?.result);
                    setTotalItems(response?.totalItems);
                }
            }
        } catch (error) {
            getErrorMessage(error, t);
        } finally {
            setLoading(false);
        }
    }, [authGet, isSelf, user?.id, t]);

    const create = useCallback(
        async (data, onSuccess, onFailure) => {
            const url = isSelf ? "/profile/working-shift" : `/users/${user?.id}/working-shift`;
            setLoading(true);
            try {
                const response = await authPost(url, {
                    data: { ...data, user: user?.id },
                });
                if (response) {
                    toast.success(t("resource-created"));
                    setData((prev) => [...prev, response]);
                    setTotalItems((prev) => (prev || 0) + 1);
                    onSuccess?.(response);
                }
            } catch (error) {
                getErrorMessage(error, t);
                onFailure?.(error);
            } finally {
                setLoading(false);
            }
        },
        [isSelf, user?.id, authPost, t]
    );

    const edit = useCallback(
        async (id, data, onSuccess, onFailure) => {
            const url = isSelf ? `/profile/working-shift/${id}` : `/users/${user?.id}/working-shift/${id}`;
            setLoading(true);
            try {
                const response = await authPut(url, {
                    data: { ...data, user: user?.id },
                });
                if (response) {
                    toast.success(t("resource-updated"));
                    setData((prev) => prev.map((item) => (item.id === id ? response : item)));
                    onSuccess?.(response);
                }
            } catch (error) {
                getErrorMessage(error, t);
                onFailure?.(error);
            } finally {
                setLoading(false);
            }
        },
        [isSelf, user?.id, authPut, t]
    );

    const remove = useCallback(
        async (id) => {
            const url = isSelf ? `/profile/working-shift/${id}` : `/users/${user?.id}/working-shift/${id}`;
            try {
                await authDelete(url);
                toast.success(t("resource-deleted"));
                setData((prev) => prev.filter((item) => item.id !== id));
                setTotalItems((prev) => prev - 1);
            } catch (error) {
                getErrorMessage(error, t);
            }
        },
        [isSelf, user?.id, authDelete, t]
    );

    return {
        fetch,
        create,
        edit,
        remove,
        totalItems,
        data,
        loading,
    };
};
